// src/components/layouts/OnboardingLayout.js
import React, { useState, useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
// import HyperrankLogo from '../ui/HyperrankLogo';
import OnboardingStepper from '../onboarding/OnboardingStepper';
import { useProjects } from '../../contexts/ProjectContext';


const OnboardingLayout = () => {
  const { user, loading: authLoading, onboardingCompleted } = useAuth();
  const location = useLocation();
  const { isCurrentProjectOnboarded, loading: projectLoading } = useProjects();
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const [isTabVisible, setIsTabVisible] = useState(true);
  
  // Handle tab visibility to prevent unnecessary renders on tab switch
  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsTabVisible(document.visibilityState === 'visible');
      
      // If coming back to visible tab, update initialLoadComplete
      if (document.visibilityState === 'visible') {
        setInitialLoadComplete(true);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);
  
  // Only show loading on first render, not on tab switching
  useEffect(() => {
    // If we have cached onboarding status, use it immediately
    const cachedStatus = localStorage.getItem('isCurrentProjectOnboarded');
    if (cachedStatus !== null && cachedStatus === 'false') {
      // If we know we're not onboarded, don't show loading
      setInitialLoadComplete(true);
    } else {
      // Otherwise use standard logic
      const isPageReload = window.performance && 
        window.performance.navigation.type === window.performance.navigation.TYPE_RELOAD;
      
      // Only delay if this is a page reload
      if (isPageReload) {
        setTimeout(() => {
          setInitialLoadComplete(true);
        }, 800);
      } else {
        // For normal navigation, don't add delay
        setInitialLoadComplete(true);
      }
    }
  }, []);
  
  // Handle dashboard redirect with delay
  useEffect(() => {
    // Skip redirect logic when tab is not visible
    if (!isTabVisible) return;
    
    // Use cached status if available on first render
    const cachedStatus = localStorage.getItem('isCurrentProjectOnboarded');
    const cachedOnboardingCompleted = user ? localStorage.getItem(`onboarding_${user.uid}`) === 'completed' : false;
    
    // Check if onboarding is completed (either from AuthContext or localStorage)
    const isOnboardingComplete = onboardingCompleted || cachedOnboardingCompleted;
    
    // On initial load, if we have a definite "true" for onboarding status,
    // wait a bit before redirecting to prevent flash of onboarding
    if (initialLoadComplete && (isOnboardingComplete || isCurrentProjectOnboarded === true)) {
      // Wait 2 seconds before deciding to redirect
      const timer = setTimeout(() => {
        // Double-check onboarding status to make sure it hasn't changed
        if (isOnboardingComplete || isCurrentProjectOnboarded === true) {
          setShouldRedirect(true);
        }
      }, 2000);
      
      return () => clearTimeout(timer);
    }
    
    // If status changes to false, clear redirect flag
    if (!isOnboardingComplete && isCurrentProjectOnboarded === false) {
      setShouldRedirect(false);
    }
  }, [isCurrentProjectOnboarded, onboardingCompleted, initialLoadComplete, isTabVisible, user]);

  // Only show loading on initial page load or when authentication is actually loading
  // Skip loading screen when tab is not visible
  if ((authLoading || projectLoading) && !initialLoadComplete && isTabVisible) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-primary-500 border-t-transparent"></div>
          <div className="mt-4 text-primary-600 text-lg font-medium">Loading...</div>
        </div>
      </div>
    );
  }

  // Delay redirect to dashboard until we're sure it's necessary
  // Only redirect when tab is visible
  const cachedOnboardingCompleted = user ? localStorage.getItem(`onboarding_${user.uid}`) === 'completed' : false;
  const isOnboardingComplete = onboardingCompleted || cachedOnboardingCompleted;
  
  if (shouldRedirect && (isOnboardingComplete || isCurrentProjectOnboarded === true) && isTabVisible) {
    // If the user is already onboarded, redirect to the dashboard
    return <Navigate to="/dashboard/content-planner" replace />;
  }

  // If not logged in, redirect to login
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Define the steps for the onboarding process
  const steps = [
    { name: 'Business', path: '/onboarding/business' },
    { name: 'Competitors', path: '/onboarding/competitors' },
    { name: 'Blog', path: '/onboarding/blog' },
    { name: 'Articles', path: '/onboarding/articles' },
    { name: 'Keywords', path: '/onboarding/keywords' },
    { name: 'Integration', path: '/onboarding/integration' }
  ];

  // Find the current step based on the URL
  const currentStepIndex = steps.findIndex(step => 
    location.pathname.includes(step.path)
  );

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header with logo */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-center ">
        
          <img 
            src="/logo.png" 
            alt="Hyperrank" 
            className="h-10"
            onError={(e) => {
              // Fallback in case the image isn't available
              e.target.onerror = null;
              e.target.style.display = 'none';
            }}
          />
        
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-grow">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          {/* Stepper */}
          <div className="mb-8">
            <OnboardingStepper steps={steps} currentStep={currentStepIndex} />
          </div>

          {/* Page content */}
          <div className="bg-white shadow rounded-lg">
            <Outlet />
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="text-center text-sm text-gray-500">
            &copy; {new Date().getFullYear()} Hyperrank. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default OnboardingLayout;